// Prospecting by phone («Τηλεφωνική προσέγγιση», owner 16 Oct 2026): «να βλέπω … πόσα τηλέφωνα παίρνω και από αυτά σε τι
// ποσοστό κλείνω συνάντηση … τη βελτίωσή μου και τι πρέπει να διαφοροποιήσω».
//
//   lists      a call list is a prospect list (schema 1) with, since schema 12, opening lines per variant (A / B), the
//              lawful basis on which the numbers are held and where they came from (consent source)
//   dial       one call_logs row per dial: local day and hour (for the statistics), duration, outcome, variant, notes;
//              the outcome moves the prospect and creates what follows — a call-back task, a follow-up, an appointment
//              in the agenda — and «do not call» is honoured everywhere (queue, import, campaigns by phone)
//   statistics dials → reached → conversations → meetings booked → held (no-shows apart) → sales; per day, week, month,
//              list and variant; best hours and weekdays; streaks and personal goals; plain-language insights computed
//              only from the advisor's own data, and only when there is enough of it to say something
//
// Definitions (shown in the help article): reached = someone answered (not interested, call back, interested, meeting,
// do not call); conversation = call back, interested or meeting, or any answered call of 60 seconds or more; meeting
// held = the appointment was completed in the agenda; no-show = marked so; sale = a policy of the person (the converted
// prospect) that started on or after the first dial.
import type { Changeset, Put, WorkspaceView } from './changeset.ts';
import { DomainError, clean, created, opt, put, timeline, touched, type Ctx } from './cmd.ts';
import { addDays, diffDays, isDay, normalizeDay, normalizeTime, startOfWeek, weekday, type Day } from './dates.ts';
import { phoneMatchKey } from './rules.ts';
import { getSetting } from './services.ts';
import { CALL_OUTCOMES, type CallLog, type CallOutcome, type CallScript, type LocationType, type Prospect, type ProspectList, type ProspectStatus, type Task } from './schema.ts';

export const CALL_VARIANTS = ['A', 'B'] as const;
export type CallVariant = typeof CALL_VARIANTS[number];
export const CALL_LAWFUL_BASES = ['consent', 'legitimate_interest', 'existing_relationship', 'unknown'] as const;
export type CallLawfulBasis = typeof CALL_LAWFUL_BASES[number];
/** someone answered */
export const REACHED_OUTCOMES: readonly CallOutcome[] = ['not_interested', 'callback', 'interested', 'meeting', 'do_not_call'];
/** a real conversation by its outcome (an answered call of 60 s or more counts too) */
export const CONVERSATION_OUTCOMES: readonly CallOutcome[] = ['callback', 'interested', 'meeting'];
export const CONVERSATION_SECONDS = 60;
export const isReached = (o: CallOutcome) => REACHED_OUTCOMES.includes(o);
export const isConversation = (l: Pick<CallLog, 'outcome' | 'durationSec'>) => CONVERSATION_OUTCOMES.includes(l.outcome) || (isReached(l.outcome) && (l.durationSec ?? 0) >= CONVERSATION_SECONDS);

/** the prospect's status after a dial with this outcome */
export const OUTCOME_STATUS: Record<CallOutcome, ProspectStatus> = {
  no_answer: 'no_answer', busy: 'no_answer', wrong_number: 'wrong_number', not_interested: 'not_interested',
  callback: 'callback', interested: 'interested', meeting: 'appointment', do_not_call: 'not_interested',
};

// --- goals & settings ------------------------------------------------------------------------------------------------
export interface CallGoals { dialsPerDay: number; meetingsPerWeek: number; maxAttempts: number }
export const DEFAULT_CALL_GOALS: CallGoals = { dialsPerDay: 40, meetingsPerWeek: 3, maxAttempts: 5 };
export const CALL_GOALS_KEY = 'calls.goals';
export function callGoals(v: WorkspaceView): CallGoals {
  const g = getSetting<Partial<CallGoals> | null>(v, CALL_GOALS_KEY, null) ?? {};
  const n = (x: unknown, d: number, max: number) => { const k = Math.round(Number(x)); return Number.isFinite(k) && k >= 1 && k <= max ? k : d; };
  return { dialsPerDay: n(g.dialsPerDay, DEFAULT_CALL_GOALS.dialsPerDay, 500), meetingsPerWeek: n(g.meetingsPerWeek, DEFAULT_CALL_GOALS.meetingsPerWeek, 100), maxAttempts: n(g.maxAttempts, DEFAULT_CALL_GOALS.maxAttempts, 20) };
}
export function normalizeCallGoals(raw: unknown): CallGoals {
  return callGoals({ rows: (t: string) => (t === 'settings' ? [{ key: CALL_GOALS_KEY, value: raw }] : []), get: () => undefined, where: () => [] } as unknown as WorkspaceView);
}

// --- do not call -----------------------------------------------------------------------------------------------------
/** the match key of a number for «do not call» (00357… = +357… = the local number) */
export const dncKey = (s: string | null | undefined) => phoneMatchKey(String(s ?? '').trim().replace(/^00/, '+'));
/** every number marked «do not call» (prospects and people), as phone match keys */
export function doNotCallKeys(v: WorkspaceView): Set<string> {
  const out = new Set<string>();
  for (const p of v.rows('prospects')) if (p.doNotCall) { const k = dncKey(p.phone); if (k) out.add(k); }
  for (const p of v.rows('persons')) if (p.doNotCall) {
    for (const ph of [p.primaryPhone, p.whatsapp, ...(p.phones ?? []).map((x) => x.value)]) { const k = dncKey(ph); if (k) out.add(k); }
  }
  return out;
}
/** is this number / person / prospect not to be called (a «do not call» anywhere wins) */
export function isDoNotCall(v: WorkspaceView, x: { phone?: string | null; personId?: string | null; prospectId?: string | null }, keys = doNotCallKeys(v)): boolean {
  if (x.personId && v.get('persons', x.personId)?.doNotCall) return true;
  if (x.prospectId) { const p = v.get('prospects', x.prospectId); if (p?.doNotCall) return true; if (p?.convertedPersonId && v.get('persons', p.convertedPersonId)?.doNotCall) return true; }
  const k = dncKey(x.phone ?? (x.prospectId ? v.get('prospects', x.prospectId)?.phone : x.personId ? v.get('persons', x.personId)?.primaryPhone : null));
  return !!k && keys.has(k);
}
/** mark or clear «do not call»; marking also cancels the open call tasks of that prospect / person */
export function setDoNotCall(v: WorkspaceView, target: { prospectId?: string | null; personId?: string | null }, value: boolean, ctx: Ctx, note?: string | null): Changeset {
  const puts: Put[] = [];
  let summary = '';
  if (target.prospectId) {
    const p = v.get('prospects', target.prospectId);
    if (!p) throw new DomainError('prospect.not_found');
    const notes = value && clean(note) ? [p.notes, `${ctx.today}: ${clean(note)}`].filter(Boolean).join('\n') : p.notes;
    puts.push(put('prospects', touched(ctx, p, { doNotCall: value, notes: notes ?? null, ...(value ? { nextFollowUpDate: null } : {}) })));
    summary = p.name;
    if (value) for (const t of v.rows('tasks')) if (t.prospectId === p.id && t.status === 'open' && t.kind === 'call') puts.push(put('tasks', touched(ctx, t, { status: 'cancelled', completedAt: ctx.now, data: { ...(t.data ?? {}), cancelledBy: 'do_not_call' } })));
  }
  if (target.personId) {
    const p = v.get('persons', target.personId);
    if (!p) throw new DomainError('person.not_found');
    puts.push(put('persons', touched(ctx, p, { doNotCall: value })));
    summary = summary || p.displayName;
    if (value) for (const t of v.where('tasks', 'personId', p.id)) if (t.status === 'open' && t.kind === 'call' && (t.origin === 'call' || t.origin === 'prospect')) puts.push(put('tasks', touched(ctx, t, { status: 'cancelled', completedAt: ctx.now, data: { ...(t.data ?? {}), cancelledBy: 'do_not_call' } })));
    puts.push(timeline(ctx, p.id, 'system', value ? 'timeline.do_not_call_on' : 'timeline.do_not_call_off', clean(note) || null));
  }
  if (!puts.length) throw new DomainError('call.no_target');
  return { action: value ? 'call.dnc_on' : 'call.dnc_off', entity: target.prospectId ? 'prospects' : 'persons', entityId: target.prospectId ?? target.personId ?? undefined, summary, puts, deletes: [] };
}

// --- call lists ------------------------------------------------------------------------------------------------------
export interface CallListInput {
  id?: string; name: string; source?: string | null; description?: string | null;
  lawfulBasis?: string | null; consentSource?: string | null; scripts?: Record<string, CallScript | null> | null;
}
const cleanScript = (s: CallScript | null | undefined): CallScript | null => {
  if (!s) return null;
  const out = { title: opt(s.title)?.slice(0, 80) ?? null, opening: opt(s.opening)?.slice(0, 2000) ?? null, notes: opt(s.notes)?.slice(0, 4000) ?? null };
  return out.title || out.opening || out.notes ? out : null;
};
/** create or update a call list: name, source, lawful basis, consent source, opening lines A / B */
export function saveCallList(v: WorkspaceView, input: CallListInput, ctx: Ctx): Changeset {
  const cur = input.id ? v.get('prospect_lists', input.id) : undefined;
  if (input.id && !cur) throw new DomainError('prospect_list.not_found');
  const name = clean(input.name ?? cur?.name);
  if (!name) throw new DomainError('prospect_list.name_required');
  const basis = input.lawfulBasis !== undefined ? opt(input.lawfulBasis) : cur?.lawfulBasis ?? null;
  if (basis && !(CALL_LAWFUL_BASES as readonly string[]).includes(basis)) throw new DomainError('call.lawful_basis');
  let scripts = cur?.scripts ?? null;
  if (input.scripts !== undefined) {
    const next: Record<string, CallScript> = {};
    for (const k of CALL_VARIANTS) { const s = cleanScript(input.scripts?.[k] ?? null); if (s) next[k] = s; }
    scripts = Object.keys(next).length ? next : null;
  }
  const fields = {
    name, source: input.source !== undefined ? opt(input.source) : cur?.source ?? null, description: input.description !== undefined ? opt(input.description) : cur?.description ?? null,
    lawfulBasis: basis, consentSource: input.consentSource !== undefined ? opt(input.consentSource) : cur?.consentSource ?? null, scripts,
  };
  const row: ProspectList = cur ? touched(ctx, cur, fields) : created<'prospect_lists'>(ctx, { ...fields, archived: false });
  return { action: cur ? 'call_list.update' : 'call_list.create', entity: 'prospect_lists', entityId: row.id, summary: row.name, puts: [put('prospect_lists', row)], deletes: [] };
}

/** the variant to use for a prospect: the one already used with them, otherwise by the chosen mode (alternate: by position) */
export function variantFor(p: Pick<Prospect, 'scriptVariant'>, mode: CallVariant | 'alternate', position: number): CallVariant {
  if (p.scriptVariant === 'A' || p.scriptVariant === 'B') return p.scriptVariant;
  return mode === 'alternate' ? (position % 2 === 0 ? 'A' : 'B') : mode;
}

export interface QueueItem { prospect: Prospect; reason: 'callback_due' | 'followup_due' | 'new' | 'retry' }
/** who to call next in a list: call-backs due, follow-ups due, new numbers, then unanswered ones with fewer attempts.
 *  Never: «do not call» (anywhere), converted, not interested, wrong number, already booked, called today, no number. */
export function callQueue(v: WorkspaceView, listId: string, today: Day, opts: { keys?: Set<string>; maxAttempts?: number } = {}): QueueItem[] {
  const keys = opts.keys ?? doNotCallKeys(v);
  const maxAttempts = opts.maxAttempts ?? callGoals(v).maxAttempts;
  const out: QueueItem[] = [];
  for (const p of v.where('prospects', 'listId', listId)) {
    if (!clean(p.phone) || p.doNotCall || isDoNotCall(v, { prospectId: p.id }, keys)) continue;
    if (p.status === 'converted' || p.status === 'not_interested' || p.status === 'wrong_number' || p.status === 'appointment') continue;
    const due = p.nextFollowUpDate && p.nextFollowUpDate <= today;
    if (p.lastCallDate === today && !due) continue;
    if (p.status === 'callback' || p.status === 'interested') { if (!p.nextFollowUpDate || due) out.push({ prospect: p, reason: p.status === 'callback' ? 'callback_due' : 'followup_due' }); continue; }
    if (p.status === 'new' || p.status === 'called') { out.push({ prospect: p, reason: 'new' }); continue; }
    if ((p.attempts ?? 0) < maxAttempts) out.push({ prospect: p, reason: 'retry' });
  }
  const rank = { callback_due: 0, followup_due: 1, new: 2, retry: 3 } as const;
  return out.sort((a, b) => rank[a.reason] - rank[b.reason] || (a.prospect.nextFollowUpDate ?? '').localeCompare(b.prospect.nextFollowUpDate ?? '') || (a.prospect.attempts ?? 0) - (b.prospect.attempts ?? 0) || a.prospect.createdAt.localeCompare(b.prospect.createdAt) || a.prospect.id.localeCompare(b.prospect.id));
}

// --- one dial --------------------------------------------------------------------------------------------------------
export interface LogCallInput {
  prospectId?: string | null; personId?: string | null; listId?: string | null;
  outcome: CallOutcome; durationSec?: number | null; variant?: string | null; notes?: string | null;
  /** the advisor's local day and hour of the dial (default: ctx.today and the hour of ctx.now) */
  day?: Day; hour?: number;
  /** call back later / interested: when (a call task); meeting: when, how long, where (an appointment in the agenda) */
  followUpDate?: string | null; followUpTime?: string | null;
  meeting?: { date: string; time: string; durationMin?: number | null; locationType?: LocationType | null; location?: string | null } | null;
  /** localized task titles (the UI passes them) */
  text?: Partial<Record<'callback' | 'followup' | 'meeting', string>>;
}
const toIsoLocal = (day: Day, time: string) => `${day}T${time}:00`;
export function logCall(v: WorkspaceView, input: LogCallInput, ctx: Ctx): Changeset {
  if (!(CALL_OUTCOMES as readonly string[]).includes(input.outcome)) throw new DomainError('call.outcome');
  const prospect = input.prospectId ? v.get('prospects', input.prospectId) : undefined;
  if (input.prospectId && !prospect) throw new DomainError('prospect.not_found');
  const personId = input.personId ?? prospect?.convertedPersonId ?? null;
  const person = personId ? v.get('persons', personId) : undefined;
  if (input.personId && !person) throw new DomainError('person.not_found');
  if (!prospect && !person) throw new DomainError('call.no_target');
  // «do not call» is honoured: no dial is logged to a number marked so (marking it again is fine)
  if (input.outcome !== 'do_not_call' && isDoNotCall(v, { prospectId: prospect?.id, personId: person?.id, phone: prospect?.phone ?? person?.primaryPhone })) throw new DomainError('call.do_not_call');
  const day = normalizeDay(input.day ?? ctx.today);
  if (!day) throw new DomainError('call.day');
  if (day > ctx.today) throw new DomainError('call.future');
  const hour = input.hour ?? new Date(ctx.now).getHours();
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) throw new DomainError('call.hour');
  const duration = input.durationSec == null ? null : Math.max(0, Math.min(4 * 3600, Math.round(Number(input.durationSec) || 0)));
  const variant = input.variant === 'A' || input.variant === 'B' ? input.variant : null;
  const listId = input.listId ?? prospect?.listId ?? null;
  const name = prospect?.name ?? person?.displayName ?? '';
  const T = input.text ?? {};
  const puts: Put[] = [];
  let taskId: string | null = null;
  let callbackAt: string | null = null;
  // what follows the call
  if (input.outcome === 'callback' || input.outcome === 'interested') {
    const date = normalizeDay(input.followUpDate) ?? (input.outcome === 'interested' ? addDays(day, 1) : null);
    if (!date) throw new DomainError('call.callback_date');
    if (date < day) throw new DomainError('call.callback_past');
    const time = normalizeTime(input.followUpTime) ?? null;
    callbackAt = time ? toIsoLocal(date, time) : null;
    const task = created<'tasks'>(ctx, {
      kind: 'call', title: (input.outcome === 'callback' ? T.callback ?? 'Call back {name}' : T.followup ?? 'Follow up {name}').replace('{name}', name),
      personId: person?.id ?? null, prospectId: prospect?.id ?? null, dueDate: date, dueTime: time, status: 'open', priority: input.outcome === 'interested' ? 'high' : 'normal',
      origin: 'call', notes: opt(input.notes), data: { callOutcome: input.outcome },
    });
    taskId = task.id; puts.push(put('tasks', task));
  } else if (input.outcome === 'meeting') {
    const m = input.meeting;
    const date = normalizeDay(m?.date), time = normalizeTime(m?.time);
    if (!date || !time) throw new DomainError('call.meeting_time');
    if (date < day) throw new DomainError('call.callback_past');
    callbackAt = toIsoLocal(date, time);
    const task = created<'tasks'>(ctx, {
      kind: 'appointment', title: (T.meeting ?? 'Meeting with {name}').replace('{name}', name), personId: person?.id ?? null, prospectId: prospect?.id ?? null,
      dueDate: date, dueTime: time, durationMin: Math.max(15, Math.min(480, Math.round(Number(m?.durationMin) || 45))), locationType: m?.locationType ?? 'office', location: opt(m?.location),
      status: 'open', priority: 'high', origin: 'call', notes: opt(input.notes), data: { callOutcome: 'meeting' },
    });
    taskId = task.id; puts.push(put('tasks', task));
  }
  const log = created<'call_logs'>(ctx, {
    listId, prospectId: prospect?.id ?? null, personId: person?.id ?? null, phone: prospect?.phone ?? person?.primaryPhone ?? null,
    at: ctx.now, day, hour, durationSec: duration, outcome: input.outcome, variant, callbackAt, taskId, notes: opt(input.notes),
  });
  puts.unshift(put('call_logs', log));
  if (taskId) { const tp = puts.find((x) => x.table === 'tasks')!; (tp.row as Task).data = { ...((tp.row as Task).data ?? {}), callLogId: log.id }; }
  if (prospect) {
    const notes = clean(input.notes) ? [prospect.notes, `${day}: ${clean(input.notes)}`].filter(Boolean).join('\n') : prospect.notes;
    const date = input.outcome === 'meeting' ? normalizeDay(input.meeting?.date) : input.outcome === 'callback' || input.outcome === 'interested' ? (normalizeDay(input.followUpDate) ?? addDays(day, 1)) : null;
    puts.push(put('prospects', touched(ctx, prospect, {
      status: prospect.status === 'converted' ? 'converted' : OUTCOME_STATUS[input.outcome], notes: notes ?? null, lastCallDate: day, attempts: (prospect.attempts ?? 0) + 1,
      scriptVariant: prospect.scriptVariant ?? variant,
      nextFollowUpDate: input.outcome === 'callback' || input.outcome === 'interested' ? date : input.outcome === 'meeting' ? null : prospect.status === 'callback' || prospect.status === 'interested' ? null : prospect.nextFollowUpDate ?? null,
      appointmentDate: input.outcome === 'meeting' ? date : prospect.appointmentDate ?? null,
      ...(input.outcome === 'do_not_call' ? { doNotCall: true } : {}),
    })));
    // the open call tasks of this prospect are done once called again (or cancelled for «do not call»)
    for (const t of v.rows('tasks')) if (t.prospectId === prospect.id && t.status === 'open' && t.kind === 'call' && t.id !== taskId && (t.dueDate ?? '') <= day) puts.push(put('tasks', touched(ctx, t, input.outcome === 'do_not_call' ? { status: 'cancelled', completedAt: ctx.now, data: { ...(t.data ?? {}), cancelledBy: 'do_not_call' } } : { status: 'done', completedAt: ctx.now })));
  }
  if (person) {
    if (input.outcome === 'do_not_call' && !person.doNotCall) puts.push(put('persons', touched(ctx, person, { doNotCall: true })));
    puts.push(timeline(ctx, person.id, 'call', `call.outcome.${input.outcome}`, opt(input.notes), { table: 'call_logs', id: log.id }));
  }
  return { action: 'call.log', entity: 'call_logs', entityId: log.id, summary: `${name} · ${input.outcome}`, puts, deletes: [] };
}

/** a meeting booked by phone took place, or the client did not come (the appointment in the agenda) */
export function markCallMeeting(v: WorkspaceView, taskId: string, result: 'held' | 'no_show', ctx: Ctx): Changeset {
  const t = v.get('tasks', taskId);
  if (!t || t.kind !== 'appointment') throw new DomainError('task.not_found');
  const row = result === 'held'
    ? touched(ctx, t, { status: 'done', completedAt: t.completedAt ?? ctx.now, outcome: t.outcome ?? 'positive', data: { ...(t.data ?? {}), noShow: false } })
    : touched(ctx, t, { status: 'cancelled', completedAt: ctx.now, outcome: 'negative', data: { ...(t.data ?? {}), noShow: true } });
  const puts: Put[] = [put('tasks', row)];
  if (result === 'no_show' && t.prospectId) { const p = v.get('prospects', t.prospectId); if (p && p.status === 'appointment') puts.push(put('prospects', touched(ctx, p, { status: 'callback', nextFollowUpDate: ctx.today }))); }
  return { action: `call.meeting_${result}`, entity: 'tasks', entityId: taskId, summary: t.title, puts, deletes: [] };
}

// --- statistics ------------------------------------------------------------------------------------------------------
export type MeetingState = 'held' | 'no_show' | 'unconfirmed' | 'upcoming' | 'cancelled';
/** what became of a meeting booked by phone, from its appointment in the agenda */
export function meetingState(v: WorkspaceView, log: Pick<CallLog, 'taskId'>, today: Day): MeetingState {
  const t = log.taskId ? v.get('tasks', log.taskId) : undefined;
  if (!t) return 'cancelled';
  if ((t.data as { noShow?: boolean } | null)?.noShow) return 'no_show';
  if (t.status === 'done') return 'held';
  if (t.status === 'cancelled') return 'cancelled';
  return (t.dueDate ?? today) < today ? 'unconfirmed' : 'upcoming';
}

export interface CallTotals {
  dials: number; reached: number; conversations: number; meetings: number; held: number; noShow: number; unconfirmed: number; upcoming: number;
  sales: number; salesPremium: number; durationSec: number;
  /** percentages (0–100, one decimal) or null when there is nothing to divide by */
  connectRate: number | null; conversationRate: number | null; meetingRate: number | null; meetingPerConversation: number | null; showRate: number | null; closeRate: number | null;
  dialsPerMeeting: number | null; avgDurationSec: number | null;
}
const pct = (a: number, b: number) => (b > 0 ? Math.round((a / b) * 1000) / 10 : null);

export interface CallFilter { from?: Day | null; to?: Day | null; listId?: string | null; variant?: string | null }
export function callLogsIn(v: WorkspaceView, f: CallFilter = {}): CallLog[] {
  return v.rows('call_logs').filter((l) => (!f.from || l.day >= f.from) && (!f.to || l.day <= f.to) && (!f.listId || l.listId === f.listId) && (!f.variant || l.variant === f.variant))
    .slice().sort((a, b) => a.at.localeCompare(b.at) || a.id.localeCompare(b.id));
}

/** the person a dialled prospect became (or the person dialled), and the first day they were dialled */
function salesOf(v: WorkspaceView, logs: readonly CallLog[]): { count: number; premium: number; persons: string[] } {
  const firstDial = new Map<string, Day>();
  for (const l of v.rows('call_logs')) {
    const pid = l.personId ?? (l.prospectId ? v.get('prospects', l.prospectId)?.convertedPersonId : null);
    if (pid && (!firstDial.has(pid) || l.day < firstDial.get(pid)!)) firstDial.set(pid, l.day);
  }
  const persons = new Set<string>();
  for (const l of logs) { const pid = l.personId ?? (l.prospectId ? v.get('prospects', l.prospectId)?.convertedPersonId : null); if (pid) persons.add(pid); }
  let count = 0, premium = 0;
  const sold: string[] = [];
  for (const pid of persons) {
    const from = firstDial.get(pid)!;
    const pols = v.where('policies', 'personId', pid).filter((p) => p.status !== 'offer' && p.status !== 'cancelled' && (p.startDate ?? p.issueDate ?? p.createdAt.slice(0, 10)) >= from);
    if (pols.length) { count++; sold.push(pid); premium += pols.reduce((s, p) => s + (Number(p.annualPremium) || 0), 0); }
  }
  return { count, premium: Math.round(premium * 100) / 100, persons: sold };
}

export function callTotals(v: WorkspaceView, logs: readonly CallLog[], today: Day): CallTotals {
  let reached = 0, conversations = 0, meetings = 0, held = 0, noShow = 0, unconfirmed = 0, upcoming = 0, durationSec = 0, timed = 0;
  for (const l of logs) {
    if (isReached(l.outcome)) reached++;
    if (isConversation(l)) conversations++;
    if (l.durationSec != null) { durationSec += l.durationSec; if (isReached(l.outcome)) timed++; }
    if (l.outcome === 'meeting') {
      meetings++;
      const s = meetingState(v, l, today);
      if (s === 'held') held++; else if (s === 'no_show') noShow++; else if (s === 'unconfirmed') unconfirmed++; else if (s === 'upcoming') upcoming++;
    }
  }
  const sales = salesOf(v, logs);
  const dials = logs.length;
  return {
    dials, reached, conversations, meetings, held, noShow, unconfirmed, upcoming, sales: sales.count, salesPremium: sales.premium, durationSec,
    connectRate: pct(reached, dials), conversationRate: pct(conversations, dials), meetingRate: pct(meetings, dials), meetingPerConversation: pct(meetings, conversations),
    showRate: pct(held, held + noShow), closeRate: pct(sales.count, held),
    dialsPerMeeting: meetings ? Math.round((dials / meetings) * 10) / 10 : null, avgDurationSec: timed ? Math.round(durationSec / timed) : null,
  };
}

export interface CallBucket { key: string; label?: string; dials: number; reached: number; conversations: number; meetings: number; meetingRate: number | null; connectRate: number | null }
const bucket = (key: string, logs: readonly CallLog[], label?: string): CallBucket => {
  const reached = logs.filter((l) => isReached(l.outcome)).length, meetings = logs.filter((l) => l.outcome === 'meeting').length;
  return { key, label, dials: logs.length, reached, conversations: logs.filter(isConversation).length, meetings, meetingRate: pct(meetings, logs.length), connectRate: pct(reached, logs.length) };
};
function groupBy<T>(xs: readonly T[], key: (x: T) => string): Map<string, T[]> { const m = new Map<string, T[]>(); for (const x of xs) { const k = key(x); const a = m.get(k); if (a) a.push(x); else m.set(k, [x]); } return m; }

export type Period = 'day' | 'week' | 'month';
/** a continuous series (days, weeks starting Monday, or months) from `from` to `to`, empty periods included */
export function callSeries(logs: readonly CallLog[], from: Day, to: Day, period: Period): CallBucket[] {
  const keyOf = (d: Day) => (period === 'day' ? d : period === 'week' ? startOfWeek(d) : d.slice(0, 7));
  const g = groupBy(logs, (l) => keyOf(l.day));
  const keys: string[] = [];
  if (period === 'month') { for (let y = Number(from.slice(0, 4)), m = Number(from.slice(5, 7)); `${y}-${String(m).padStart(2, '0')}` <= to.slice(0, 7); m === 12 ? (y++, m = 1) : m++) keys.push(`${y}-${String(m).padStart(2, '0')}`); }
  else { const step = period === 'day' ? 1 : 7; for (let d = keyOf(from); d <= to; d = addDays(d, step)) keys.push(d); }
  return keys.slice(-400).map((k) => bucket(k, g.get(k) ?? []));
}

/** two-hour windows from 08:00 to 21:00 for the «best hours» view */
export const HOUR_WINDOWS: [number, number][] = [[8, 10], [10, 12], [12, 14], [14, 17], [17, 19], [19, 21]];
const windowOf = (h: number) => HOUR_WINDOWS.findIndex(([a, b]) => h >= a && h < b);

export interface Insight { key: string; tone: 'good' | 'watch' | 'info'; params: Record<string, string | number> }
export interface CallStats {
  filter: CallFilter; totals: CallTotals;
  funnel: { key: 'dials' | 'reached' | 'conversations' | 'meetings' | 'held' | 'sales'; value: number }[];
  byList: (CallBucket & { listId: string | null })[]; byVariant: CallBucket[]; byHour: CallBucket[]; byWindow: CallBucket[]; byWeekday: CallBucket[];
  goals: CallGoals & { dialsToday: number; meetingsThisWeek: number; dialsThisWeek: number };
  streak: { current: number; best: number };
  overdueCallbacks: Task[]; insights: Insight[];
}

/** the open call-back / follow-up tasks from calls that are past their day */
export function overdueCallbacks(v: WorkspaceView, today: Day): Task[] {
  return v.rows('tasks').filter((t) => t.status === 'open' && t.kind === 'call' && t.origin === 'call' && !!t.dueDate && t.dueDate < today).sort((a, b) => (a.dueDate ?? '').localeCompare(b.dueDate ?? ''));
}

/** days in a row (counting back from today, or from yesterday while today's goal is not reached yet) with the daily dial goal met;
 *  Saturdays and Sundays without calls do not break a streak */
export function callStreak(v: WorkspaceView, today: Day, goal: number): { current: number; best: number } {
  const perDay = new Map<string, number>();
  for (const l of v.rows('call_logs')) perDay.set(l.day, (perDay.get(l.day) ?? 0) + 1);
  const met = (d: Day) => (perDay.get(d) ?? 0) >= goal;
  const weekend = (d: Day) => { const w = weekday(d); return w === 0 || w === 6; };
  let current = 0;
  let d = met(today) ? today : addDays(today, -1);
  for (let i = 0; i < 400; i++, d = addDays(d, -1)) { if (met(d)) current++; else if (weekend(d) && !perDay.get(d)) continue; else break; }
  let best = 0, run = 0;
  const days = [...perDay.keys()].sort();
  if (days.length) for (let x = days[0]!; x <= today; x = addDays(x, 1)) { if (met(x)) { run++; best = Math.max(best, run); } else if (!(weekend(x) && !perDay.get(x))) run = 0; }
  return { current, best: Math.max(best, current) };
}

const MIN_DIALS_FOR_RATE = 15;
/** plain-language observations, computed only from the advisor's data and only with enough calls behind them */
export function callInsights(v: WorkspaceView, s: Omit<CallStats, 'insights'>, today: Day): Insight[] {
  const out: Insight[] = [];
  const t = s.totals;
  const overall = t.meetingRate ?? 0;
  // best two-hour window for meetings
  if (t.meetings >= 3) {
    const best = s.byWindow.filter((w) => w.dials >= MIN_DIALS_FOR_RATE && w.meetingRate != null).sort((a, b) => (b.meetingRate! - a.meetingRate!) || b.dials - a.dials)[0];
    if (best && best.meetingRate! >= overall * 1.4 && best.meetingRate! - overall >= 1) out.push({ key: 'best_window', tone: 'good', params: { window: best.key, rate: best.meetingRate!, overall } });
  }
  // best weekday
  if (t.meetings >= 3) {
    const best = s.byWeekday.filter((w) => w.dials >= MIN_DIALS_FOR_RATE && w.meetingRate != null).sort((a, b) => (b.meetingRate! - a.meetingRate!) || b.dials - a.dials)[0];
    if (best && best.meetingRate! >= overall * 1.4 && best.meetingRate! - overall >= 1) out.push({ key: 'best_weekday', tone: 'good', params: { weekday: Number(best.key), rate: best.meetingRate!, overall } });
  }
  // A vs B
  const A = s.byVariant.find((x) => x.key === 'A'), B = s.byVariant.find((x) => x.key === 'B');
  if (A && B && A.dials >= 20 && B.dials >= 20 && (A.meetings + B.meetings) >= 4) {
    const [win, lose] = (B.meetingRate ?? 0) >= (A.meetingRate ?? 0) ? [B, A] : [A, B];
    const ratio = lose.meetingRate ? Math.round((win.meetingRate! / lose.meetingRate) * 10) / 10 : null;
    if (ratio === null && win.meetings >= 3) out.push({ key: 'variant_only', tone: 'good', params: { win: win.key, lose: lose.key, meetings: win.meetings } });
    else if (ratio !== null && ratio >= 1.5) out.push({ key: 'variant_ratio', tone: 'good', params: { win: win.key, lose: lose.key, ratio, winRate: win.meetingRate!, loseRate: lose.meetingRate! } });
    else if (ratio !== null) out.push({ key: 'variant_even', tone: 'info', params: { a: A.meetingRate ?? 0, b: B.meetingRate ?? 0 } });
  } else if ((A?.dials ?? 0) + (B?.dials ?? 0) > 0 && (!A || !B || A.dials < 20 || B.dials < 20)) {
    out.push({ key: 'variant_more_data', tone: 'info', params: { a: A?.dials ?? 0, b: B?.dials ?? 0, need: 20 } });
  }
  // call-backs never followed up
  if (s.overdueCallbacks.length >= 3) out.push({ key: 'callbacks_overdue', tone: 'watch', params: { count: s.overdueCallbacks.length } });
  // few answered: suggest the best connect window
  if (t.dials >= 30 && t.connectRate != null && t.connectRate < 30) {
    const best = s.byWindow.filter((w) => w.dials >= MIN_DIALS_FOR_RATE && w.connectRate != null).sort((a, b) => b.connectRate! - a.connectRate!)[0];
    out.push({ key: best && best.connectRate! > t.connectRate + 5 ? 'connect_low_window' : 'connect_low', tone: 'watch', params: { rate: t.connectRate, window: best?.key ?? '', best: best?.connectRate ?? 0 } });
  }
  // no-shows
  if (t.held + t.noShow >= 4 && t.noShow / (t.held + t.noShow) >= 0.3) out.push({ key: 'no_shows', tone: 'watch', params: { noShow: t.noShow, total: t.held + t.noShow } });
  if (t.unconfirmed >= 2) out.push({ key: 'meetings_unconfirmed', tone: 'watch', params: { count: t.unconfirmed } });
  // this week against the four weeks before
  const wk = startOfWeek(today);
  const recent = v.rows('call_logs').filter((l) => l.day >= addDays(wk, -28) && l.day < wk && (!s.filter.listId || l.listId === s.filter.listId));
  const thisWeek = v.rows('call_logs').filter((l) => l.day >= wk && l.day <= today && (!s.filter.listId || l.listId === s.filter.listId));
  if (recent.length >= 40 && thisWeek.length >= 15) {
    const before = pct(recent.filter((l) => l.outcome === 'meeting').length, recent.length) ?? 0, now = pct(thisWeek.filter((l) => l.outcome === 'meeting').length, thisWeek.length) ?? 0;
    if (now - before >= 1.5) out.push({ key: 'trend_up', tone: 'good', params: { now, before } });
    else if (before - now >= 1.5) out.push({ key: 'trend_down', tone: 'watch', params: { now, before } });
  }
  if (t.dialsPerMeeting != null && t.meetings >= 3) out.push({ key: 'dials_per_meeting', tone: 'info', params: { dials: t.dialsPerMeeting } });
  if (s.streak.current >= 3) out.push({ key: 'streak', tone: 'good', params: { days: s.streak.current } });
  if (t.sales > 0) out.push({ key: 'sales', tone: 'good', params: { sales: t.sales, meetings: t.held } });
  if (!t.dials) out.push({ key: 'no_data', tone: 'info', params: {} });
  return out;
}

export function callStats(v: WorkspaceView, filter: CallFilter, today: Day): CallStats {
  const logs = callLogsIn(v, filter);
  const totals = callTotals(v, logs, today);
  const lists = groupBy(logs, (l) => l.listId ?? '');
  const byList = [...lists.entries()].map(([id, ls]) => ({ ...bucket(id, ls, id ? v.get('prospect_lists', id)?.name ?? '—' : ''), listId: id || null })).sort((a, b) => b.dials - a.dials);
  const byVariant = [...groupBy(logs.filter((l) => l.variant), (l) => l.variant!).entries()].map(([k, ls]) => bucket(k, ls)).sort((a, b) => a.key.localeCompare(b.key));
  const hours = groupBy(logs, (l) => String(l.hour));
  const byHour = Array.from({ length: 14 }, (_, i) => i + 8).map((h) => bucket(String(h), hours.get(String(h)) ?? []));
  const wins = groupBy(logs.filter((l) => windowOf(l.hour) >= 0), (l) => String(windowOf(l.hour)));
  const byWindow = HOUR_WINDOWS.map(([a, b], i) => bucket(`${String(a).padStart(2, '0')}:00–${String(b).padStart(2, '0')}:00`, wins.get(String(i)) ?? []));
  const wds = groupBy(logs, (l) => String(weekday(l.day)));
  const byWeekday = [1, 2, 3, 4, 5, 6, 0].map((d) => bucket(String(d), wds.get(String(d)) ?? []));
  const g = callGoals(v);
  const wk = startOfWeek(today);
  const all = v.rows('call_logs');
  const goals = { ...g, dialsToday: all.filter((l) => l.day === today).length, dialsThisWeek: all.filter((l) => l.day >= wk && l.day <= today).length, meetingsThisWeek: all.filter((l) => l.day >= wk && l.day <= today && l.outcome === 'meeting').length };
  const streak = callStreak(v, today, g.dialsPerDay);
  const overdue = overdueCallbacks(v, today);
  const funnel = [
    { key: 'dials' as const, value: totals.dials }, { key: 'reached' as const, value: totals.reached }, { key: 'conversations' as const, value: totals.conversations },
    { key: 'meetings' as const, value: totals.meetings }, { key: 'held' as const, value: totals.held }, { key: 'sales' as const, value: totals.sales },
  ];
  const base = { filter, totals, funnel, byList, byVariant, byHour, byWindow, byWeekday, goals, streak, overdueCallbacks: overdue };
  return { ...base, insights: callInsights(v, base, today) };
}

/** a preset period ending today: this week, last 30 days, this month, last 90 days, this year */
export function callPeriod(kind: 'week' | '30d' | 'month' | '90d' | 'year', today: Day): { from: Day; to: Day } {
  if (kind === 'week') return { from: startOfWeek(today), to: today };
  if (kind === 'month') return { from: `${today.slice(0, 7)}-01`, to: today };
  if (kind === 'year') return { from: `${today.slice(0, 4)}-01-01`, to: today };
  return { from: addDays(today, kind === '30d' ? -29 : -89), to: today };
}
/** the period to chart for a range: days up to 45 days, weeks up to 26 weeks, months beyond */
export const periodFor = (from: Day, to: Day): Period => { const n = diffDays(from, to); return n <= 45 ? 'day' : n <= 182 ? 'week' : 'month'; };

/** rows for a CSV export of the dials (header row first; labels come from the UI) */
export function callLogRows(v: WorkspaceView, logs: readonly CallLog[], label: { outcome: (o: CallOutcome) => string; header: string[] }): (string | number)[][] {
  const rows: (string | number)[][] = [label.header];
  for (const l of logs) {
    const p = l.prospectId ? v.get('prospects', l.prospectId) : undefined, person = l.personId ? v.get('persons', l.personId) : undefined;
    rows.push([l.day, `${String(l.hour).padStart(2, '0')}:00`, l.listId ? v.get('prospect_lists', l.listId)?.name ?? '' : '', p?.name ?? person?.displayName ?? '', l.phone ?? '', label.outcome(l.outcome), l.variant ?? '', l.durationSec ?? '', l.callbackAt ? l.callbackAt.replace('T', ' ').slice(0, 16) : '', l.notes ?? '']);
  }
  return rows;
}
export const isCallDay = (d: unknown): d is Day => isDay(d);
